'use client'
import { getOrderItemsForReview, IOrderItemsForReview } from '@/features/reviews/services'
import { Box } from '@mui/material'
import { useRouter } from 'next/navigation'
import { parseAsString, useQueryState } from 'nuqs'
import { useEffect, useState } from 'react'
import { OrderInfoForm, PopupOTPConfirm, PopupPolicy, ReviewForm, SuccessModal } from './components'
import { useAccountStore } from '@/store'

type Props = {}

type StepType = 'ORDER_INFO' | 'REVIEW_FORM' | 'OTP_CONFIRM' | 'SUCCESS'

export const ReviewOrder = (props: Props) => {
  const router = useRouter()
  const { updateUser } = useAccountStore()
  const [ordercodeParam, setOrderCodeParam] = useQueryState('ordercode', parseAsString.withDefault(''))
  const [step, setStep] = useState<StepType>('ORDER_INFO')
  const [orderData, setOrderData] = useState<IOrderItemsForReview | null>(null)
  const [policyOpen, setPolicyOpen] = useState(false)
  const [otpOpen, setOtpOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)

  // Loading & Error states
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Form states
  const [orderCode, setOrderCode] = useState('')
  const [protectCode, setProtectCode] = useState('') // Thêm protect_code
  const [verificationToken, setVerificationToken] = useState<string | null>(null)
  const [isLoginRequired, setIsLoginRequired] = useState(false)

  // Handler: Gửi mã đơn hàng
  const handleOrderSubmit = async () => {
    setLoading(true)
    setErrorMessage('')

    const result = await getOrderItemsForReview({
      increment_id: orderCode,
      protect_code: protectCode,
    })

    setLoading(false)
    setIsLoginRequired(result.isLoginRequired || false)

    if (result.status === 'error') {
      setErrorMessage(result.message || 'Không thể lấy thông tin đơn hàng')
      return
    }

    if (result.data) {
      setOrderData(result.data)
      // Tự động mở popup chính sách sau khi validate thành công
      setPolicyOpen(true)
    }
  }
  useEffect(() => {
    if (ordercodeParam) {
      setOrderCode(ordercodeParam)
      handleOrderSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordercodeParam])
  // Handler: Đóng popup chính sách và chuyển sang step 2
  const handlePolicyAccept = () => {
    setPolicyOpen(false)
    setStep('REVIEW_FORM')
  }

  // Handler: Gửi đánh giá - callback từ ReviewForm
  const handleReviewSubmitSuccess = async (data: {
    status: string
    verification_token?: string | null
    message?: string
  }) => {
    // Kiểm tra nếu cần OTP (Guest checkout)
    if (data.status === 'OTP_REQUIRED' && data.verification_token) {
      setVerificationToken(data.verification_token)
      setOtpOpen(true)
    } else if (data.status === 'SUCCESS') {
      // Nếu đã login thì thành công luôn
      setSuccessOpen(true)
    }
  }

  // Handler: Xác thực OTP thành công
  const handleOTPVerifySuccess = () => {
    setOtpOpen(false)
    setSuccessOpen(true)
  }

  // Handler: Gửi lại OTP
  const handleResendOTP = async () => {
    // TODO: Implement API riêng cho resend OTP
    // Hiện tại chưa có endpoint này từ backend
    console.log('Resend OTP for verification_token:', verificationToken)
  }

  const handleSuccessConfirm = async () => {
    await updateUser()
    router.push('/')
  }

  // Render based on step
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
      }}
    >
      {/* Main Content */}
      {step === 'ORDER_INFO' && (
        <OrderInfoForm
          orderCode={orderCode}
          setOrderCode={setOrderCode}
          onSubmit={handleOrderSubmit}
          loading={loading}
          errorMessage={errorMessage}
          isLoginRequired={isLoginRequired}
        />
      )}

      {step === 'REVIEW_FORM' && orderData && (
        <ReviewForm
          orderData={orderData}
          protectCode={protectCode}
          onSubmitSuccess={handleReviewSubmitSuccess}
          onBack={() => setStep('ORDER_INFO')}
        />
      )}

      {/* Popups */}
      <PopupPolicy
        isOpen={policyOpen}
        handleClose={() => setPolicyOpen(false)}
        onAccept={handlePolicyAccept}
      />
      <PopupOTPConfirm
        isOpen={otpOpen}
        handleClose={() => setOtpOpen(false)}
        verificationToken={verificationToken}
        onVerifySuccess={handleOTPVerifySuccess}
        onResendOTP={handleResendOTP}
      />
      <SuccessModal isOpen={successOpen} onConfirm={handleSuccessConfirm} />
    </Box>
  )
}
